<template>
  <div v-if="stores.length > 0 && mainContentLoaded" class="max-w-5xl mx-auto px-4 sm:px-0">
    <h2 class="text-2xl font-bold text-gray-brand-900 tracking-tighter cursor-pointer align-middle mb-4">
      {{ titleText }}
    </h2>
    <div class="relative flex items-center w-full px-4 sm:px-0">
      <button
        v-if="shouldShowArrows"
        :disabled="!canScrollLeft"
        @click="scrollLeft"
        class="absolute z-10 rounded-full w-10 h-10 sm:w-12 sm:h-12 shadow-solid-xs border flex items-center justify-center transition-shadow left-0 sm:left-[-72px] top-1/2 -translate-y-1/2"
        :class="[
          canScrollLeft
            ? 'bg-white border-gray-brand-600 shadow-gray-brand-600 cursor-pointer hover:shadow-lg'
            : 'bg-gray-100 border-gray-300 shadow-gray-300 cursor-not-allowed opacity-50'
        ]"
        aria-label="Scroll left"
      >
        <ChevronLeftIcon class="h-5 w-5 sm:h-6 sm:w-6" />
      </button>
      <div
        ref="carouselContainer"
        class="max-w-5xl mx-auto flex gap-3 overflow-x-auto scrollbar-hide items-stretch justify-between"
        :style="{ ...containerStyle, scrollBehavior: 'smooth' }"
      >
        <div
          v-fade-in
          v-for="store in stores"
          :key="store.id"
          class="flex-shrink-0 flex items-stretch"
          :style="
            isMobile ? { width: '100%', minWidth: '100%' } : { width: 'calc(50% - 6px)', minWidth: 'calc(50% - 6px)' }
          "
        >
          <div class="w-full pl-1 pb-1 flex">
            <div
              class="relative bg-white w-full rounded-lg border cursor-pointer overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
              :class="cardShadowClasses"
              @click="(e) => go(e, store.id)"
              style="height: 100%"
            >
              <div class="px-6 py-4 flex flex-col justify-between w-full flex-1">
                <div class="flex justify-between items-start w-full mb-2">
                  <div class="flex justify-start space-x-2 items-center flex-1">
                    <div v-if="store.attributes.locked">
                      <LockOpenIcon v-if="store.attributes.customer_has_access" class="h-5" />
                      <LockClosedIcon v-else class="h-5" />
                    </div>
                    <p class="font-bold text-xl leading-5 tracking-tighter">
                      {{ store.attributes.store_name }}
                    </p>
                  </div>
                  <div class="flex-shrink-0 ml-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </div>
                </div>
                <div
                  v-if="store.attributes.store_description && store.attributes.store_description.length > 0"
                  class="flex-1"
                >
                  <p
                    v-linkified:options="{ defaultProtocol: 'https' }"
                    class="text-base text-gray-500 whitespace-pre-line"
                  >
                    {{ store.attributes.store_description }}
                  </p>
                </div>
                <div v-else class="flex-1"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="shouldShowArrows"
        :disabled="!canScrollRight"
        @click="scrollRight"
        class="absolute z-10 rounded-full w-10 h-10 sm:w-12 sm:h-12 shadow-solid-xs border flex items-center justify-center transition-shadow right-0 sm:right-[-72px] top-1/2 -translate-y-1/2"
        :class="[
          canScrollRight
            ? 'bg-white border-gray-brand-600 shadow-gray-brand-600 cursor-pointer hover:shadow-lg'
            : 'bg-gray-100 border-gray-300 shadow-gray-300 cursor-not-allowed opacity-50'
        ]"
        aria-label="Scroll right"
      >
        <ChevronRightIcon class="h-5 w-5 sm:h-6 sm:w-6" />
      </button>
    </div>
  </div>
</template>
<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { LockClosedIcon, LockOpenIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/outline'
import { cardShadowClasses } from '@/config'

export default {
  name: 'StoreListCarousel',
  components: { LockClosedIcon, LockOpenIcon, ChevronLeftIcon, ChevronRightIcon },
  setup() {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const carouselContainer = ref(null)

    const mainContentLoaded = computed(() => store.getters.mainContentLoaded)

    const currentStoreId = computed(() => {
      const routeStoreId = route.params.storeId
      const bottleStoreId = store.getters.storeId
      return routeStoreId || bottleStoreId
    })

    const stores = computed(() => {
      const allStores = store.getters.stores
        .filter((store) => store.attributes.is_active && store.attributes.is_public)
        .filter((storeItem) => {
          const currentId = String(currentStoreId.value || '')
          const itemId = String(storeItem.id || '')
          return itemId !== currentId
        })
        .sort((a, b) => a.attributes.order - b.attributes.order)
      return allStores
    })

    const canScrollLeft = ref(false)
    const canScrollRight = ref(false)
    const isMobile = ref(false)

    const shouldShowArrows = computed(() => {
      if (isMobile.value) {
        return stores.value.length > 1
      } else {
        return stores.value.length > 2
      }
    })

    const titleText = computed(() => {
      return stores.value.length === 1 ? 'View our other menu' : 'View our other menus'
    })

    const containerStyle = computed(() => {
      if (isMobile.value) {
        return { width: 'calc(100% - 80px)', margin: '0 auto' }
      }
      return { width: '100%', margin: '0 auto' }
    })

    const updateScrollButtons = () => {
      if (!carouselContainer.value) return
      const container = carouselContainer.value
      canScrollLeft.value = container.scrollLeft > 0
      canScrollRight.value = container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    }

    const scrollLeft = () => {
      if (!carouselContainer.value || !canScrollLeft.value) return
      const container = carouselContainer.value
      const containerWidth = container.clientWidth
      const isMobile = window.innerWidth < 640

      if (isMobile) {
        const cardWidth = containerWidth
        const gap = 12
        const currentScroll = container.scrollLeft
        const currentCardIndex = Math.round(currentScroll / (cardWidth + gap))
        const targetScroll = Math.max(0, (currentCardIndex - 1) * (cardWidth + gap))
        container.scrollTo({ left: targetScroll, behavior: 'smooth' })
      } else {
        const cardWidth = (containerWidth - 12) / 2 + 12
        container.scrollBy({ left: -cardWidth, behavior: 'smooth' })
      }
    }

    const scrollRight = () => {
      if (!carouselContainer.value || !canScrollRight.value) return
      const container = carouselContainer.value
      const containerWidth = container.clientWidth
      const isMobile = window.innerWidth < 640

      if (isMobile) {
        const cardWidth = containerWidth
        const gap = 12
        const currentScroll = container.scrollLeft
        const currentCardIndex = Math.round(currentScroll / (cardWidth + gap))
        const maxScroll = container.scrollWidth - container.clientWidth
        const targetScroll = Math.min(maxScroll, (currentCardIndex + 1) * (cardWidth + gap))
        container.scrollTo({ left: targetScroll, behavior: 'smooth' })
      } else {
        const cardWidth = (containerWidth - 12) / 2 + 12
        container.scrollBy({ left: cardWidth, behavior: 'smooth' })
      }
    }

    const checkMobile = () => {
      isMobile.value = window.innerWidth < 640
    }

    onMounted(async () => {
      await store.dispatch('getStores')
      checkMobile()
      window.addEventListener('resize', checkMobile)

      setTimeout(() => {
        updateScrollButtons()
        if (carouselContainer.value) {
          carouselContainer.value.addEventListener('scroll', updateScrollButtons)
        }
      }, 100)
    })

    watch(stores, () => {
      setTimeout(() => {
        updateScrollButtons()
      }, 100)
    })

    const go = async (e, storeId) => {
      if (e && e.target && e.target.tagName === 'A') return
      window.bottle.hooks.v1.emit('view_item_list', {
        item_list_id: storeId,
        item_list_name: stores.value.find((s) => s.id === storeId).attributes.store_name
      })
      router.push({ name: 'BottleFactory', params: { storeId: storeId } })
    }

    return {
      mainContentLoaded,
      stores,
      go,
      carouselContainer,
      scrollLeft,
      scrollRight,
      canScrollLeft,
      canScrollRight,
      containerStyle,
      isMobile,
      shouldShowArrows,
      titleText,
      cardShadowClasses
    }
  }
}
</script>
<style>
.linkified {
  @apply text-blue-500;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
